import React from 'react'
import { GridColDef, GridValueFormatterParams } from '@mui/x-data-grid'
import {
  PaginationResponse,
  UserScreeningRecord,
  UserScreeningRecordScreeningTypeEnum,
  UserScreeningStatuses,
} from '../../swagger'
import { dateToSlashString } from '../../utils/dateUtility'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../Table/ActionableTable'
import { useTranslation } from 'react-i18next'

export enum UserScreeningSortBy {
  ProviderUserId = 'providerUserId',
  UserEmail = 'userEmail',
  ScreeningType = 'screeningType',
  Status = 'status',
  DateRequested = 'dateRequested',
  StatusDate = 'statusDate',
  ValidTo = 'validTo',
}

const UserScreeningTable: React.FC<{
  userScreenings: UserScreeningRecord[]
  isLoading?: boolean
  handlePaginationChange: (pagination: PaginationResponse) => void
  pagination: PaginationResponse
  handleRowAction: (
    status: UserScreeningStatuses,
    userScreeningKey: number,
    screeningTye: UserScreeningRecordScreeningTypeEnum
  ) => void
}> = ({
  userScreenings,
  handlePaginationChange,
  pagination,
  handleRowAction,
}): React.JSX.Element => {
  const { t } = useTranslation()

  const columnHeadersOptions: Pick<
    GridColDef,
    'align' | 'sortable' | 'sortingOrder'
  > = {
    align: 'left',
    sortable: true,
    sortingOrder: ['asc', 'desc'],
  }

  const ActionableTableColumnsHeader: ActionableTableColumn[] = [
    {
      fieldName: 'providerUserId',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.ProviderUserId',
        'Provider User Id'
      ),
      ...columnHeadersOptions,
    },
    {
      fieldName: 'userEmail',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.UserEmail',
        'User Email'
      ),
      ...columnHeadersOptions,
    },
    {
      fieldName: 'screeningType',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.ScreeningType',
        'Screening Type'
      ),
      ...columnHeadersOptions,
    },
    {
      fieldName: 'status',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.Status',
        'Status'
      ),
      ...columnHeadersOptions,
    },
    {
      fieldName: 'dateRequested',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.RequestedDate',
        'Requested Date'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return `${dateToSlashString(params.value)}`
      },
      ...columnHeadersOptions,
    },
    {
      fieldName: 'statusDate',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.StatusDate',
        'Status Date'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return `${dateToSlashString(params.value)}`
      },
      ...columnHeadersOptions,
    },
    {
      fieldName: 'validTo',
      columnHeaderName: t(
        'Legal.UserScreeningTab.Table.Header.ValidTo',
        'Valid To Date'
      ),
      valueFormatter: (params: GridValueFormatterParams<Date>) => {
        return `${dateToSlashString(params.value)}`
      },
      sortable: false,
    },
  ]

  const rowActions = [
    {
      actionName: t(
        'Legal.userScreenings.Table.RowAction.ApprovedManual',
        '{{buttonText}}',
        { buttonText: UserScreeningStatuses.ApprovedManual }
      ),
      actionFunction: (userScreeningRecord: UserScreeningRecord) => {
        if (
          userScreeningRecord.status !== UserScreeningStatuses.ApprovedManual
        ) {
          handleRowAction(
            UserScreeningStatuses.ApprovedManual,
            userScreeningRecord.userScreeningKey,
            userScreeningRecord.screeningType
          )
        }
      },
      actionKey: UserScreeningStatuses.ApprovedManual,
    },
    {
      actionName: t(
        'Legal.userScreenings.Table.RowAction.DeclinedManual',
        '{{buttonText}}',
        { buttonText: UserScreeningStatuses.DeclinedManual }
      ),
      actionFunction: (userScreeningRecord: UserScreeningRecord) => {
        if (
          userScreeningRecord.status !== UserScreeningStatuses.DeclinedManual
        ) {
          handleRowAction(
            UserScreeningStatuses.DeclinedManual,
            userScreeningRecord.userScreeningKey,
            userScreeningRecord.screeningType
          )
        }
      },
      actionKey: UserScreeningStatuses.DeclinedManual,
    },
  ]

  const handleSorting = (
    pageInformation: PaginationResponse
  ): PaginationResponse => {
    const orderBy = pageInformation.orderBy?.[0]

    for (const key in orderBy) {
      let orderByProperty = key
      switch (key) {
        case UserScreeningSortBy.ProviderUserId:
          orderByProperty = UserScreeningSortBy.ProviderUserId
          break
        case UserScreeningSortBy.UserEmail:
          orderByProperty = UserScreeningSortBy.UserEmail
          break
        case UserScreeningSortBy.ScreeningType:
          orderByProperty = UserScreeningSortBy.ScreeningType
          break
        case UserScreeningSortBy.Status:
          orderByProperty = UserScreeningSortBy.Status
          break
        case UserScreeningSortBy.DateRequested:
          orderByProperty = UserScreeningSortBy.DateRequested
          break
        case UserScreeningSortBy.StatusDate:
          orderByProperty = UserScreeningSortBy.StatusDate
          break
        case UserScreeningSortBy.ValidTo:
          orderByProperty = UserScreeningSortBy.ValidTo
          break
        default:
          break
      }

      return {
        ...pageInformation,
        orderBy: [{ [orderByProperty]: orderBy[key] }],
      }
    }

    return pageInformation
  }

  return (
    <ActionableTable
      columns={ActionableTableColumnsHeader}
      rows={userScreenings}
      rowActions={rowActions.length ? rowActions : undefined}
      noResultsMessage={t(
        'Legal.UserScreeningTab.Table.NoUserScreenings',
        'No user screenings found'
      )}
      pagination={{
        ...pagination,
        page: pagination.page - 1,
      }}
      handlePaginationChange={(pagination) => {
        pagination = handleSorting(pagination)
        return handlePaginationChange(pagination)
      }}
    />
  )
}

export default UserScreeningTable
